import React, { useState, useCallback, useEffect } from "react";
import client from "@/api/client";
import { toast } from "sonner";
import { Package, Zap, TrendingDown, Clock, DollarSign, RefreshCw, Send, ChevronDown, ChevronRight, Download, Share2, AlertTriangle } from "lucide-react";
import UpgradeModal from "@/components/UpgradeModal";
import CreditPotentialPanel from "@/components/CreditPotentialPanel";

const ACTION_LABELS = {
  solar_pv: "Solar PV",
  energy_efficiency: "Energy Efficiency",
  fuel_switch: "Fuel Switch",
  fleet_electrification: "Fleet Electrification",
  waste_reduction: "Waste Reduction",
  materials_optimization: "Low-Carbon Materials",
};
const ACTION_COLORS = {
  solar_pv: "bg-yellow-50 border-yellow-200 text-yellow-800",
  energy_efficiency: "bg-blue-50 border-blue-200 text-blue-800",
  fuel_switch: "bg-orange-50 border-orange-200 text-orange-800",
  fleet_electrification: "bg-violet-50 border-violet-200 text-violet-800",
  waste_reduction: "bg-teal-50 border-teal-200 text-teal-800",
  materials_optimization: "bg-green-50 border-green-200 text-green-800",
};
const GRADE_CONFIG = {
  strong:   { color: "text-emerald-700 bg-emerald-50",  label: "Strong ROI" },
  good:     { color: "text-blue-700 bg-blue-50",        label: "Good ROI" },
  moderate: { color: "text-amber-700 bg-amber-50",      label: "Moderate ROI" },
  marginal: { color: "text-slate-600 bg-slate-100",     label: "Marginal ROI" },
};
const CONF_DOT = { high: "bg-emerald-500", medium: "bg-amber-400", low: "bg-red-400" };

function ActionCard({ action, index }) {
  const [expanded, setExpanded] = useState(false);
  const ac = ACTION_COLORS[action.action_type] || "bg-slate-50 border-slate-200 text-slate-700";
  return (
    <div className={`rounded-xl border p-4 ${ac}`} data-testid={`action-card-${index}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold">{ACTION_LABELS[action.action_type] || action.action_type}</span>
            <span className="text-xs opacity-60">#{index + 1}</span>
            <span className={`ml-1 inline-flex items-center gap-1 text-xs`}>
              <span className={`w-1.5 h-1.5 rounded-full ${CONF_DOT[action.confidence_level] || "bg-slate-400"}`} />
              {action.confidence_level}
            </span>
          </div>
          <p className="text-xs opacity-75 leading-relaxed">{action.description}</p>
        </div>
        <span className="text-sm font-bold flex-shrink-0">{action.priority_score?.toFixed(0)}<span className="text-xs font-normal opacity-60">/100</span></span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        {[
          { icon: DollarSign, label: "Capex", value: `$${(action.capex_required || 0).toLocaleString()}` },
          { icon: TrendingDown, label: "CO₂/yr", value: `${(action.co2_reduction || 0).toFixed(1)} t` },
          { icon: Clock, label: "Payback", value: `${(action.payback_years || 0).toFixed(1)} yrs` },
          { icon: Zap, label: "ROI", value: `${(action.roi_pct || 0).toFixed(0)}%` },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="bg-white/60 rounded-lg p-2 text-center">
            <p className="text-xs opacity-60">{label}</p>
            <p className="text-sm font-bold">{value}</p>
          </div>
        ))}
      </div>

      <button onClick={() => setExpanded(!expanded)} className="mt-2 flex items-center gap-1 text-xs opacity-60 hover:opacity-80">
        {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        {expanded ? "Hide" : "Show"} derivation
      </button>
      {expanded && (
        <div className="mt-2 text-xs opacity-70 bg-white/50 rounded p-2 leading-relaxed">
          {action.derivation_notes}
        </div>
      )}
    </div>
  );
}

export default function InvestmentPackPanel({ assessmentId }) {
  const [actions, setActions] = useState([]);
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState({ actions: false, pack: false, submit: false });
  const [submitNotes, setSubmitNotes] = useState("");
  const [showSubmit, setShowSubmit] = useState(false);
  const [frs, setFrs] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const loadExisting = useCallback(async () => {
    try {
      const [aRes, pRes, frsRes] = await Promise.allSettled([
        client.get(`/actions/assessments/${assessmentId}`),
        client.get(`/packs/assessments/${assessmentId}/latest`),
        client.get(`/financing/readiness/${assessmentId}`),
      ]);
      if (aRes.status === "fulfilled") setActions(aRes.value.data?.actions || []);
      if (pRes.status === "fulfilled") setPack(pRes.value.data);
      if (frsRes.status === "fulfilled") setFrs(frsRes.value.data);
    } catch {}
  }, [assessmentId]);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  const generateActions = useCallback(async () => {
    setLoading(l => ({ ...l, actions: true }));
    try {
      const res = await client.post(`/actions/assessments/${assessmentId}/generate`);
      setActions(res.data.actions || []);
      toast.success(`${res.data.action_count} action recommendations generated.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Action generation failed"); }
    setLoading(l => ({ ...l, actions: false }));
  }, [assessmentId]);

  const generatePack = useCallback(async () => {
    setLoading(l => ({ ...l, pack: true }));
    try {
      const res = await client.post(`/packs/assessments/${assessmentId}/generate`);
      setPack(res.data);
      toast.success(`Investment Pack ${res.data.pack_id} generated (v${res.data.version}).`);
    } catch (err) { toast.error(err.response?.data?.detail || "Pack generation failed"); }
    setLoading(l => ({ ...l, pack: false }));
  }, [assessmentId]);

  const downloadMemo = useCallback(async () => {
    if (!pack?.pack_id) return;
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const res = await fetch(`${API}/api/packs/${pack.pack_id}/memo.pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${pack.pack_id}-investment-memo.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Investment Memo downloaded.");
    } catch { toast.error("Failed to download memo PDF"); }
  }, [pack]);

  const submitForFinancing = useCallback(async () => {
    if (!pack?.pack_id) return;
    setLoading(l => ({ ...l, submit: true }));
    try {
      const res = await client.post(`/financing/packs/${pack.pack_id}/submit`, { notes: submitNotes });
      if (res.data.review_id) {
        toast.success(`Submitted for financing review. ID: ${res.data.review_id}`);
        setShowSubmit(false);
        loadExisting();
      } else {
        toast.info(res.data.message);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Submission failed"); }
    setLoading(l => ({ ...l, submit: false }));
  }, [pack, submitNotes, loadExisting]);

  const investCase = pack?.investment_case || {};
  const gradeCfg = GRADE_CONFIG[investCase.investment_grade] || GRADE_CONFIG.moderate;

  return (
    <>
    <div className="space-y-4">
      {/* Actions Section */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Zap size={15} className="text-[#059669]" />
            <h3 className="text-sm font-semibold text-[#0F172A]">Action Recommendations</h3>
            {actions.length > 0 && <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">{actions.length}</span>}
          </div>
          <button onClick={generateActions} disabled={loading.actions} data-testid="generate-actions-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0F172A] text-white text-xs font-medium rounded-lg hover:bg-slate-800 disabled:opacity-60">
            <RefreshCw size={12} className={loading.actions ? "animate-spin" : ""} />
            {actions.length > 0 ? "Regenerate" : "Generate Actions"}
          </button>
        </div>

        {actions.length > 0 ? (
          <div className="space-y-3">
            {actions.map((a, i) => <ActionCard key={a.id || i} action={a} index={i} />)}
            <p className="text-xs text-slate-400 italic mt-2">
              All financial estimates are rule-based (deterministic). No AI used for core calculations.
              Reference ranges per IRENA, IEA, UNEP benchmarks.
            </p>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <Zap size={28} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Generate action recommendations derived from emission hotspots.</p>
          </div>
        )}
      </div>

      {/* Pack Generation */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Package size={15} className="text-[#059669]" />
            <h3 className="text-sm font-semibold text-[#0F172A]">Investment Pack</h3>
            {pack && <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-200">v{pack.version}</span>}
          </div>
          <button onClick={generatePack} disabled={loading.pack} data-testid="generate-pack-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#059669] text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-60">
            <RefreshCw size={12} className={loading.pack ? "animate-spin" : ""} />
            {pack ? "Regenerate Pack" : "Generate Pack"}
          </button>
        </div>

        {pack ? (
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <p className="font-mono text-xs font-bold text-[#0F172A]">{pack.pack_id}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${gradeCfg.color}`}>
                  {gradeCfg.label}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${pack.status === "submitted" || pack.status === "approved" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                  {pack.status}
                </span>
                <button onClick={downloadMemo} data-testid="download-memo-btn"
                  className="ml-auto flex items-center gap-1 px-2.5 py-1 bg-[#0F172A] text-white text-xs font-medium rounded-lg hover:bg-slate-800">
                  <Download size={11} /> Download Memo
                </button>
                <button onClick={() => {
                  const url = `${window.location.origin}/verify/pack/${pack.pack_id}`;
                  navigator.clipboard.writeText(url).then(() => toast.success("Public link copied to clipboard!"));
                }} data-testid="share-pack-btn"
                  className="flex items-center gap-1 px-2.5 py-1 border border-slate-300 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-50">
                  <Share2 size={11} /> Share
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Total Capex", value: `$${(investCase.total_capex_usd || 0).toLocaleString()}` },
                  { label: "Annual Savings", value: `$${(investCase.total_annual_savings_usd || 0).toLocaleString()}` },
                  { label: "CO₂ Reduction", value: `${(investCase.total_co2_reduction_tco2e_yr || 0).toFixed(1)} t/yr` },
                  { label: "Payback", value: `${(investCase.weighted_payback_years || 0).toFixed(1)} yrs` },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-400">{label}</p>
                    <p className="text-base font-bold text-[#0F172A]">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {pack.status === "generated" && (
              <>
                {/* Submission Readiness Gate */}
                {frs && !frs.submission_gate?.can_submit && (
                  <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 mb-3" data-testid="submission-gate">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs font-semibold text-amber-800">Not yet ready for financing submission</p>
                    </div>
                    <div className="space-y-1 pl-5">
                      {!frs.submission_gate?.frs_ok && (
                        <p className="text-xs text-amber-700">• Increase Financing Readiness Score to 50+ (currently {(frs.score||0).toFixed(0)})</p>
                      )}
                      {!frs.submission_gate?.verif_ok && (
                        <p className="text-xs text-amber-700">• Complete system validation in the Intelligence tab</p>
                      )}
                    </div>
                  </div>
                )}

                {!showSubmit ? (
                  <button onClick={() => {
                    if (frs && !frs.submission_gate?.can_submit) {
                      toast.warning("Address the readiness requirements first.");
                      return;
                    }
                    setShowSubmit(true);
                  }} data-testid="submit-financing-btn"
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                      frs && !frs.submission_gate?.can_submit
                        ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                        : "bg-[#0F172A] text-white hover:bg-slate-800"
                    }`}>
                    <Send size={14} /> Submit for Financing Review
                  </button>
                ) : (
                  <div className="border border-slate-200 rounded-xl p-4">
                    <p className="text-sm font-medium text-[#0F172A] mb-2">Submit for Financing</p>
                    <textarea value={submitNotes} onChange={e => setSubmitNotes(e.target.value)} rows={2}
                      placeholder="Optional notes for the FI reviewer..."
                      className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-[#059669]" />
                    <div className="flex gap-2 mt-2">
                      <button onClick={submitForFinancing} disabled={loading.submit}
                        className="flex-1 px-3 py-2 bg-[#059669] text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-60">
                        {loading.submit ? "Submitting..." : "Confirm Submission"}
                      </button>
                      <button onClick={() => setShowSubmit(false)} className="px-3 py-2 border border-slate-200 text-slate-600 text-xs rounded-lg hover:bg-slate-50">Cancel</button>
                    </div>
                  </div>
                )}
              </>
            )}
            {pack.status !== "generated" && (
              <div className="px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700">
                Pack is in financing review — status: <strong>{pack.status}</strong>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <Package size={28} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Bundle V1 emissions + confidence + actions into an Investment Pack.</p>
          </div>
        )}
      </div>

      {/* Carbon Credits — additional revenue stream */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Additional Revenue Stream</p>
        <CreditPotentialPanel assessmentId={assessmentId} />
      </div>
    </div>
    {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} frsScore={frs?.score} frsGrade={frs?.grade} />}
    </>
  );
}
